// src/pages/CitationsPage.jsx
// Citations Module - domain citation analysis across brand audits

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../components/AppShell";
import UserActionsBar from "../components/UserActionsBar";
import {
  citationsBrands,
  citationsBrandDetail,
  citationsBrandAudits,
  citationsDomainDetail,
  citationsDomainUrls,
  citationsSearch,
  citationsAddOwnedDomain,
  citationsRemoveOwnedDomain,
} from "../services/api";

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------
const GOLD = "#FFCC33";
const NAVY = "#1a1f36";
const SIDEBAR_W = 280;

const CATEGORY_COLORS = {
  news: { bg: "#e0f2fe", color: "#0369a1" },
  blog: { bg: "#f0fdf4", color: "#15803d" },
  social: { bg: "#fef3c7", color: "#92400e" },
  ecommerce: { bg: "#fce7f3", color: "#9d174d" },
  government: { bg: "#ede9fe", color: "#6d28d9" },
  academic: { bg: "#e0e7ff", color: "#3730a3" },
  forum: { bg: "#fff7ed", color: "#c2410c" },
  video: { bg: "#fdf2f8", color: "#be185d" },
  reference: { bg: "#f0f9ff", color: "#0c4a6e" },
  competitor: { bg: "#fee2e2", color: "#991b1b" },
  owned: { bg: "#dcfce7", color: "#166534" },
};

function categoryStyle(cat) {
  const c = cat ? cat.toLowerCase() : "";
  return CATEGORY_COLORS[c] || { bg: "#f3f4f6", color: "#4b5563" };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(iso) {
  if (!iso) return "--";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function truncateUrl(url, max = 60) {
  if (!url) return "--";
  if (url.length <= max) return url;
  return url.slice(0, max - 3) + "...";
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SortHeader({ label, field, sortField, sortDir, onSort, style }) {
  const active = sortField === field;
  return (
    <th
      onClick={() => onSort(field)}
      style={{
        cursor: "pointer",
        userSelect: "none",
        padding: "10px 12px",
        textAlign: "left",
        fontSize: 12,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        color: active ? NAVY : "#666",
        borderBottom: "2px solid #e5e7eb",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {label}
      {active && (
        <span style={{ marginLeft: 4, fontSize: 10 }}>
          {sortDir === "asc" ? "\u25B2" : "\u25BC"}
        </span>
      )}
    </th>
  );
}

function CategoryBadge({ category }) {
  const s = categoryStyle(category);
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
        textTransform: "capitalize",
      }}
    >
      {category || "unknown"}
    </span>
  );
}

function SummaryCard({ label, value, icon }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: "16px 20px",
        flex: "1 1 0",
        minWidth: 150,
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 10,
          background: `linear-gradient(135deg, ${NAVY}, #2a2f4a)`,
          color: GOLD,
          display: "grid",
          placeItems: "center",
          fontSize: 18,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: NAVY }}>{value ?? "--"}</div>
        <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
      <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.4 }}>{"\uD83D\uDD17"}</div>
      <p style={{ fontSize: 16, margin: 0 }}>{message}</p>
    </div>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || "Search domains..."}
      style={{
        width: "100%",
        padding: "10px 14px",
        borderRadius: 8,
        border: "1px solid #ddd",
        fontSize: 14,
        outline: "none",
        boxSizing: "border-box",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Brand sidebar folder
// ---------------------------------------------------------------------------

function BrandFolder({ brand, count, selected, onClick, expanded, onToggle, audits, selectedAuditId, onSelectAudit }) {
  const isAll = brand === null;
  return (
    <div>
      <button
        onClick={onClick}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "10px 14px",
          borderRadius: 8,
          border: "none",
          background: selected
            ? `linear-gradient(135deg, ${NAVY}, #2a2f4a)`
            : "transparent",
          color: selected ? "#fff" : NAVY,
          cursor: "pointer",
          fontSize: 14,
          fontWeight: selected ? 700 : 500,
          textAlign: "left",
          transition: "background 0.15s",
        }}
      >
        <span style={{ fontSize: 16, opacity: 0.8 }}>
          {isAll ? "\uD83C\uDF10" : "\uD83D\uDCC2"}
        </span>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {isAll ? "All Brands" : (brand?.brand_name || `Brand #${brand?.brand_id}`)}
        </span>
        {count > 0 && (
          <span
            style={{
              background: selected ? GOLD : "#e8e8e8",
              color: selected ? NAVY : "#555",
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 10,
              padding: "2px 8px",
              minWidth: 22,
              textAlign: "center",
            }}
          >
            {count}
          </span>
        )}
        {!isAll && selected && audits && audits.length > 0 && (
          <span
            onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
            style={{ fontSize: 10, padding: "2px 4px", cursor: "pointer" }}
          >
            {expanded ? "\u25BC" : "\u25B6"}
          </span>
        )}
      </button>

      {/* Audit sub-items */}
      {!isAll && selected && expanded && audits && audits.length > 0 && (
        <div style={{ paddingLeft: 36, display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
          <button
            onClick={() => onSelectAudit?.(null)}
            style={{
              background: selectedAuditId === null ? "rgba(255,204,51,0.15)" : "transparent",
              border: "none",
              borderRadius: 6,
              padding: "6px 10px",
              fontSize: 12,
              fontWeight: selectedAuditId === null ? 700 : 400,
              color: NAVY,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            All Audits
          </button>
          {audits.map((a) => (
            <button
              key={a.audit_id}
              onClick={() => onSelectAudit?.(a.audit_id)}
              style={{
                background: selectedAuditId === a.audit_id ? "rgba(255,204,51,0.15)" : "transparent",
                border: "none",
                borderRadius: 6,
                padding: "6px 10px",
                fontSize: 12,
                fontWeight: selectedAuditId === a.audit_id ? 700 : 400,
                color: NAVY,
                cursor: "pointer",
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={a.title || `Audit #${a.audit_id}`}
            >
              {a.title || `Audit #${a.audit_id}`}
              <span style={{ fontSize: 10, color: "#888", marginLeft: 6 }}>
                ({a.citations || 0})
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Domain detail modal (cross-brand + URL list)
// ---------------------------------------------------------------------------

function DomainModal({ domain, brandId, brandName, auditId, onClose }) {
  const [domainData, setDomainData] = useState(null);
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewAllBrands, setViewAllBrands] = useState(!brandId); // if no brand selected, show all

  // Drilldown state: shows per-audit breakdown when a count is clicked
  const [drilldown, setDrilldown] = useState(null);

  const effectiveBrandId = viewAllBrands ? null : brandId;

  useEffect(() => {
    if (!domain) return;
    setLoading(true);
    setDrilldown(null);
    Promise.all([
      citationsDomainDetail(domain).catch(() => null),
      citationsDomainUrls(domain, effectiveBrandId, auditId).catch(() => null),
    ]).then(([detail, urlData]) => {
      setDomainData(detail);
      setUrls(urlData?.urls || []);
      setLoading(false);
    });
  }, [domain, effectiveBrandId, auditId]);

  const openDrilldown = async (label, drillDomain, drillUrl, drillBrandId) => {
    setDrilldown({ label, items: [], loading: true });
    try {
      const { citationsAuditDetail } = await import("../services/api");
      const data = await citationsAuditDetail(drillDomain, drillUrl, drillBrandId);
      setDrilldown({ label, items: data?.items || [], loading: false });
    } catch {
      setDrilldown({ label, items: [], loading: false });
    }
  };

  if (!domain) return null;

  const clickableCount = (value, label, drillDomain, drillUrl, drillBrandId) => (
    <button
      onClick={() => openDrilldown(label, drillDomain, drillUrl, drillBrandId)}
      style={{
        background: "none", border: "none", cursor: "pointer", color: "#2563eb",
        fontWeight: 600, fontSize: 13, textDecoration: "underline", padding: 0,
      }}
      title="View per-audit breakdown"
    >
      {value}
    </button>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
        display: "grid", placeItems: "center", padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16, width: "100%", maxWidth: 860,
          maxHeight: "85vh", overflowY: "auto", padding: 0,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 24px", borderBottom: "1px solid #e5e7eb",
          display: "flex", alignItems: "center", gap: 12,
          position: "sticky", top: 0, background: "#fff", zIndex: 1, borderRadius: "16px 16px 0 0",
        }}>
          {drilldown ? (
            <>
              <button
                onClick={() => setDrilldown(null)}
                style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#6b7280", padding: "2px 8px" }}
              >{"\u2190"}</button>
              <span style={{ fontSize: 16, fontWeight: 700, color: NAVY, flex: 1 }}>{drilldown.label}</span>
            </>
          ) : (
            <>
              <span style={{ fontSize: 20, fontWeight: 700, color: NAVY, flex: 1 }}>{domain}</span>
              {domainData?.category && <CategoryBadge category={domainData.category} />}
            </>
          )}
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#999", padding: "4px 8px", lineHeight: 1 }}>
            {"\u00D7"}
          </button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          {loading && <div style={{ textAlign: "center", padding: 30, color: "#888" }}>Loading...</div>}

          {/* Drilldown view: per-audit breakdown */}
          {!loading && drilldown && (
            <div>
              {drilldown.loading ? (
                <div style={{ textAlign: "center", padding: 30, color: "#888" }}>Loading audit details...</div>
              ) : drilldown.items.length === 0 ? (
                <div style={{ color: "#999", fontSize: 13, padding: 8 }}>No audit data found.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={modalThStyle}>Brand</th>
                      <th style={modalThStyle}>Audit</th>
                      <th style={modalThStyle}>Date</th>
                      <th style={{ ...modalThStyle, textAlign: "right" }}>Appearances</th>
                      <th style={{ ...modalThStyle, textAlign: "right" }}>Models</th>
                      <th style={{ ...modalThStyle, textAlign: "right" }}>Questions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldown.items.map((a, i) => (
                      <tr key={i}>
                        <td style={{ ...modalTdStyle, fontWeight: 600 }}>{a.brand_name}</td>
                        <td style={modalTdStyle}>{a.audit_title || `#${a.audit_id}`}</td>
                        <td style={{ ...modalTdStyle, fontSize: 12, color: "#6b7280" }}>
                          {a.audit_date ? new Date(a.audit_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                        </td>
                        <td style={{ ...modalTdStyle, textAlign: "right", fontWeight: 600 }}>{a.appearances}</td>
                        <td style={{ ...modalTdStyle, textAlign: "right" }}>{a.models}</td>
                        <td style={{ ...modalTdStyle, textAlign: "right" }}>{a.questions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Main view */}
          {!loading && !drilldown && (
            <>
              {/* Section 1: Brands citing this domain */}
              {domainData?.brands && domainData.brands.length > 0 && (
                <div style={{ marginBottom: 28 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: 0 }}>
                      Brands citing this domain
                    </h3>
                    {brandId && (
                      <button
                        onClick={() => setViewAllBrands((v) => !v)}
                        style={{
                          fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 6, cursor: "pointer",
                          background: viewAllBrands ? NAVY : "#f3f4f6",
                          color: viewAllBrands ? "#fff" : "#374151",
                          border: viewAllBrands ? `1px solid ${NAVY}` : "1px solid #d1d5db",
                          transition: "all 0.15s",
                        }}
                      >
                        {viewAllBrands ? "Showing all brands" : "Show all brands"}
                      </button>
                    )}
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={modalThStyle}>Brand</th>
                        <th style={{ ...modalThStyle, textAlign: "right" }}>Appearances</th>
                        <th style={{ ...modalThStyle, textAlign: "right" }}>Audit Spread</th>
                      </tr>
                    </thead>
                    <tbody>
                      {domainData.brands.map((b, i) => (
                        <tr key={i}>
                          <td style={modalTdStyle}>{b.brand_name}</td>
                          <td style={{ ...modalTdStyle, textAlign: "right" }}>
                            {clickableCount(b.appearances, `${domain} - ${b.brand_name} - appearances`, domain, null, b.brand_id)}
                          </td>
                          <td style={{ ...modalTdStyle, textAlign: "right" }}>
                            {clickableCount(b.audit_spread, `${domain} - ${b.brand_name} - per audit`, domain, null, b.brand_id)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Section 2: Full URLs */}
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: "0 0 12px" }}>
                  Full URLs ({urls.length})
                  {!viewAllBrands && brandName && (
                    <span style={{ fontWeight: 400, fontSize: 13, color: "#6b7280" }}> for {brandName}</span>
                  )}
                  {viewAllBrands && brandId && (
                    <span style={{ fontWeight: 400, fontSize: 13, color: "#6b7280" }}> across all brands</span>
                  )}
                </h3>
                {urls.length === 0 && (
                  <div style={{ color: "#999", fontSize: 13, padding: 8 }}>No URLs found.</div>
                )}
                {urls.length > 0 && (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={modalThStyle}>URL</th>
                        <th style={modalThStyle}>Brands</th>
                        <th style={{ ...modalThStyle, textAlign: "right", width: 90 }}>Appearances</th>
                        <th style={{ ...modalThStyle, textAlign: "right", width: 100 }}>Audit Spread</th>
                      </tr>
                    </thead>
                    <tbody>
                      {urls.map((u, i) => (
                        <tr key={i}>
                          <td style={modalTdStyle} title={u.url}>
                            <a href={u.url} target="_blank" rel="noopener noreferrer"
                              style={{ color: "#2563eb", textDecoration: "none", fontSize: 13 }}>
                              {truncateUrl(u.url, 55)}
                            </a>
                          </td>
                          <td style={{ ...modalTdStyle, fontSize: 12 }}>
                            {(u.brands || []).map((b, j) => (
                              <span key={j} style={{
                                display: "inline-block", background: "#f0f4ff", color: "#3b5998",
                                padding: "1px 6px", borderRadius: 3, fontSize: 11, marginRight: 3, marginBottom: 2,
                              }}>
                                {b.name} ({b.appearances})
                              </span>
                            ))}
                          </td>
                          <td style={{ ...modalTdStyle, textAlign: "right" }}>
                            {clickableCount(u.appearances, `${truncateUrl(u.url, 40)} - appearances`, null, u.url, brandId)}
                          </td>
                          <td style={{ ...modalTdStyle, textAlign: "right" }}>
                            {clickableCount(u.audit_spread, `${truncateUrl(u.url, 40)} - per audit`, null, u.url, brandId)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const modalThStyle = {
  padding: "8px 10px",
  textAlign: "left",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: "#666",
  borderBottom: "2px solid #e5e7eb",
};

const modalTdStyle = {
  padding: "8px 10px",
  fontSize: 13,
  borderBottom: "1px solid #f3f4f6",
  color: NAVY,
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function CitationsPage({ currentUser }) {
  const navigate = useNavigate();

  // Sidebar state
  const [brandsData, setBrandsData] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [selectedAuditId, setSelectedAuditId] = useState(null);
  const [auditsExpanded, setAuditsExpanded] = useState(true);
  const [brandAudits, setBrandAudits] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Main data
  const [domains, setDomains] = useState([]);
  const [ownedDomains, setOwnedDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const searchTimer = useRef(null);

  // Filters
  const [filterCategory, setFilterCategory] = useState("");

  // Sort
  const [sortField, setSortField] = useState("appearances");
  const [sortDir, setSortDir] = useState("desc");

  // Edit mode (admin only)
  const [editMode, setEditMode] = useState(false);
  const [selectedForReclassify, setSelectedForReclassify] = useState(new Set());
  const [reclassifying, setReclassifying] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type: "success"|"error", message}

  // Modal
  const [modalDomain, setModalDomain] = useState(null);

  // Responsive
  const [narrow, setNarrow] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isAdmin = currentUser?.is_admin;

  // ---- Load brands list ----
  const loadBrands = useCallback(async () => {
    try {
      const data = await citationsBrands();
      setBrandsData(data?.items || []);
    } catch (err) {
      console.error("Citations: failed to load brands", err);
    }
  }, []);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // ---- Load audits when brand selected ----
  useEffect(() => {
    if (!selectedBrandId) {
      setBrandAudits([]);
      return;
    }
    citationsBrandAudits(selectedBrandId)
      .then((data) => setBrandAudits(data?.items || []))
      .catch(() => setBrandAudits([]));
  }, [selectedBrandId]);

  // ---- Load domain data ----
  const loadDomains = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!selectedBrandId) {
        // "All Brands" - show aggregate from brands data
        // We don't have a single endpoint for all domains across all brands,
        // so we show the brand list summary. Domains load when a brand is picked.
        setDomains([]);
        setOwnedDomains([]);
      } else {
        const data = await citationsBrandDetail(selectedBrandId, selectedAuditId);
        setDomains(data?.domains || []);
        setOwnedDomains(data?.owned_domains || []);
      }
    } catch (err) {
      console.error("Citations: failed to load domains", err);
      setError(err?.message || "Failed to load citation data");
    } finally {
      setLoading(false);
    }
  }, [selectedBrandId, selectedAuditId]);

  useEffect(() => {
    setSearchResults(null);
    setSearch("");
    loadDomains();
  }, [loadDomains]);

  // ---- Debounced search ----
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (!search.trim()) {
      setSearchResults(null);
      return;
    }

    searchTimer.current = setTimeout(async () => {
      try {
        const data = await citationsSearch(search.trim(), selectedBrandId || undefined);
        setSearchResults(data?.results || []);
      } catch {
        setSearchResults([]);
      }
    }, 300);

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [search, selectedBrandId]);

  // ---- Sorting ----
  const handleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return field;
      }
      setSortDir("desc");
      return field;
    });
  }, []);

  // ---- Owned domain toggle ----
  const toggleOwned = useCallback(async (domain, isCurrentlyOwned) => {
    if (!selectedBrandId || !isAdmin) return;
    try {
      if (isCurrentlyOwned) {
        await citationsRemoveOwnedDomain(selectedBrandId, domain);
        setOwnedDomains((prev) => prev.filter((d) => d !== domain));
      } else {
        await citationsAddOwnedDomain(selectedBrandId, domain);
        setOwnedDomains((prev) => [...prev, domain]);
      }
    } catch (err) {
      console.error("Failed to toggle owned domain:", err);
    }
  }, [selectedBrandId, isAdmin]);

  // ---- Derived: visible domains ----
  const displayDomains = useMemo(() => {
    let source = searchResults !== null ? searchResults : domains;
    if (filterCategory) {
      source = source.filter((d) => d.category === filterCategory);
    }
    const sorted = [...source].sort((a, b) => {
      let av = a[sortField];
      let bv = b[sortField];
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [domains, searchResults, sortField, sortDir, filterCategory]);

  // Unique categories present in current domain list (for filter dropdown)
  const availableCategories = useMemo(() => {
    const source = searchResults !== null ? searchResults : domains;
    const cats = new Set(source.map((d) => d.category).filter(Boolean));
    return [...cats].sort();
  }, [domains, searchResults]);

  // ---- Summary stats ----
  const summary = useMemo(() => {
    if (selectedBrandId && domains.length > 0) {
      return {
        totalDomains: domains.length,
        totalCitations: domains.reduce((s, d) => s + (d.appearances || 0), 0),
        auditSpread: Math.max(...domains.map((d) => d.audit_spread || 0), 0),
        uniqueUrls: domains.reduce((s, d) => s + (d.unique_urls || 0), 0),
      };
    }
    if (!selectedBrandId && brandsData.length > 0) {
      return {
        totalDomains: brandsData.reduce((s, b) => s + (b.unique_urls || 0), 0),
        totalCitations: brandsData.reduce((s, b) => s + (b.total_citations || 0), 0),
        auditSpread: brandsData.reduce((s, b) => s + (b.audits || 0), 0),
        uniqueUrls: brandsData.reduce((s, b) => s + (b.unique_urls || 0), 0),
      };
    }
    return { totalDomains: 0, totalCitations: 0, auditSpread: 0, uniqueUrls: 0 };
  }, [selectedBrandId, domains, brandsData]);

  const showSidebar = narrow ? sidebarOpen : true;

  // ---- Render ----
  return (
    <AppShell
      headerSubtitle="Citations"
      headerRight={
        <UserActionsBar
          mode="module"
          currentUser={currentUser}
          onBackToModules={() => navigate("/modules")}
        />
      }
    >
      {/* Mobile toggle */}
      {narrow && (
        <button
          onClick={() => setSidebarOpen((v) => !v)}
          style={{
            marginBottom: 12,
            padding: "8px 16px",
            borderRadius: 8,
            border: `1px solid ${NAVY}`,
            background: sidebarOpen ? NAVY : "#fff",
            color: sidebarOpen ? "#fff" : NAVY,
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          {sidebarOpen ? "Hide Brands" : "Show Brands"} ({brandsData.length})
        </button>
      )}

      {error && (
        <div
          style={{
            background: "#fff0f0",
            color: "#c00",
            padding: "12px 16px",
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          {error}
          <button
            onClick={loadDomains}
            style={{
              marginLeft: 12,
              padding: "4px 12px",
              borderRadius: 6,
              border: "1px solid #c00",
              background: "transparent",
              color: "#c00",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: 20, minHeight: 400 }}>
        {/* ---- Sidebar ---- */}
        {showSidebar && (
          <div
            style={{
              width: narrow ? "100%" : SIDEBAR_W,
              minWidth: narrow ? undefined : SIDEBAR_W,
              flexShrink: 0,
              background: "#fafafa",
              borderRadius: 12,
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              overflowY: "auto",
              maxHeight: narrow ? 300 : "calc(100vh - 260px)",
              border: "1px solid #eee",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "#888",
                padding: "6px 14px 8px",
              }}
            >
              Brands
            </div>

            <BrandFolder
              brand={null}
              count={brandsData.reduce((s, b) => s + (b.total_citations || 0), 0)}
              selected={selectedBrandId === null}
              onClick={() => {
                setSelectedBrandId(null);
                setSelectedAuditId(null);
                if (narrow) setSidebarOpen(false);
              }}
            />

            {brandsData.map((b) => (
              <BrandFolder
                key={b.brand_id}
                brand={b}
                count={b.total_citations || 0}
                selected={selectedBrandId === b.brand_id}
                onClick={() => {
                  setSelectedBrandId(b.brand_id);
                  setSelectedAuditId(null);
                  setAuditsExpanded(true);
                  if (narrow) setSidebarOpen(false);
                }}
                expanded={auditsExpanded && selectedBrandId === b.brand_id}
                onToggle={() => setAuditsExpanded((v) => !v)}
                audits={selectedBrandId === b.brand_id ? brandAudits : []}
                selectedAuditId={selectedAuditId}
                onSelectAudit={setSelectedAuditId}
              />
            ))}

            {brandsData.length === 0 && !loading && (
              <div style={{ padding: "12px 14px", fontSize: 13, color: "#999" }}>
                No citation data available
              </div>
            )}
          </div>
        )}

        {/* ---- Main area ---- */}
        {(!narrow || !sidebarOpen) && (
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Summary cards */}
            <div
              style={{
                display: "flex",
                gap: 14,
                marginBottom: 20,
                flexWrap: "wrap",
              }}
            >
              <SummaryCard label="Total Domains" value={summary.totalDomains} icon={"\uD83C\uDF10"} />
              <SummaryCard label="Total Citations" value={summary.totalCitations} icon={"\uD83D\uDD17"} />
              <SummaryCard label="Audit Spread" value={summary.auditSpread} icon={"\uD83D\uDCCA"} />
              <SummaryCard label="Unique URLs" value={summary.uniqueUrls} icon={"\uD83D\uDD0D"} />
            </div>

            {/* Header row with search */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 700,
                  color: NAVY,
                  flex: 1,
                  minWidth: 140,
                }}
              >
                {selectedBrandId === null
                  ? "All Brands Overview"
                  : brandsData.find((b) => b.brand_id === selectedBrandId)?.brand_name || "Domains"}
                <span
                  style={{ fontSize: 14, fontWeight: 400, color: "#888", marginLeft: 10 }}
                >
                  ({displayDomains.length})
                </span>
              </h2>

              <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
                {/* Category filter */}
                {availableCategories.length > 1 && (
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    style={{
                      padding: "7px 12px", fontSize: 13, borderRadius: 8,
                      border: "1px solid #d1d5db", background: filterCategory ? "#eff6ff" : "#fff",
                      color: filterCategory ? "#1d4ed8" : "#374151", fontWeight: filterCategory ? 600 : 400,
                      cursor: "pointer", minWidth: 160,
                    }}
                  >
                    <option value="">All categories</option>
                    {availableCategories.map((c) => (
                      <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                )}

                {/* Search */}
                {selectedBrandId && (
                  <div style={{ width: 240 }}>
                    <SearchBar
                      value={search}
                      onChange={setSearch}
                      placeholder="Search domains..."
                    />
                  </div>
                )}

                {/* Edit mode toggle (admin only) */}
                {currentUser?.is_admin && (
                  <button
                    onClick={() => { setEditMode((v) => !v); setSelectedForReclassify(new Set()); }}
                    style={{
                      padding: "7px 14px", fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: "pointer",
                      background: editMode ? "#dc2626" : "#f3f4f6",
                      color: editMode ? "#fff" : "#374151",
                      border: editMode ? "1px solid #dc2626" : "1px solid #d1d5db",
                    }}
                  >
                    {editMode ? "Exit Edit Mode" : "Edit Mode"}
                  </button>
                )}

                {/* Reclassify selected (visible in edit mode) */}
                {editMode && selectedForReclassify.size > 0 && (
                  <button
                    onClick={async () => {
                      setReclassifying(true);
                      setFeedback(null);
                      try {
                        const { citationsReclassify, citationsBrandDetail } = await import("../services/api");
                        const result = await citationsReclassify([...selectedForReclassify]);
                        const count = result?.reclassified || 0;
                        const results = result?.results || {};
                        // Build summary
                        const actions = Object.values(results);
                        const heuristic = actions.filter((a) => a.action?.includes("heuristic")).length;
                        const llm = actions.filter((a) => a.action?.includes("LLM")).length;
                        const fallback = actions.filter((a) => a.action?.includes("fallback")).length;
                        let msg = `Reclassified ${count} domain${count !== 1 ? "s" : ""}`;
                        const parts = [];
                        if (heuristic) parts.push(`${heuristic} via heuristic`);
                        if (llm) parts.push(`${llm} via LLM`);
                        if (fallback) parts.push(`${fallback} fallback`);
                        if (parts.length) msg += ` (${parts.join(", ")})`;
                        setFeedback({ type: "success", message: msg });

                        setSelectedForReclassify(new Set());
                        // Refresh domains
                        if (selectedBrandId) {
                          const data = await citationsBrandDetail(selectedBrandId, selectedAuditId);
                          setDomains(data?.domains || []);
                        }
                        // Update local domain categories
                        setDomains((prev) => prev.map((d) => {
                          const r = results[d.domain];
                          return r ? { ...d, category: r.category } : d;
                        }));
                      } catch (err) {
                        setFeedback({ type: "error", message: err?.message || "Reclassification failed" });
                      }
                      setReclassifying(false);
                    }}
                    disabled={reclassifying}
                    style={{
                      padding: "7px 14px", fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: "pointer",
                      background: "#7c3aed", color: "#fff", border: "1px solid #7c3aed",
                      opacity: reclassifying ? 0.6 : 1,
                    }}
                  >
                    {reclassifying ? "Reclassifying..." : `Reclassify ${selectedForReclassify.size} selected`}
                  </button>
                )}
              </div>
            </div>

            {/* Feedback banner */}
            {feedback && (
              <div
                style={{
                  padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 500,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: feedback.type === "error" ? "#fef2f2" : "#f0fdf4",
                  color: feedback.type === "error" ? "#991b1b" : "#166534",
                  border: `1px solid ${feedback.type === "error" ? "#fecaca" : "#bbf7d0"}`,
                }}
              >
                <span>{feedback.message}</span>
                <button onClick={() => setFeedback(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "inherit", padding: "0 4px" }}>{"\u00D7"}</button>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div style={{ padding: 40, textAlign: "center", color: "#888" }}>
                Loading citation data...
              </div>
            )}

            {/* "All brands" view - brand summary table */}
            {!loading && selectedBrandId === null && (
              <>
                {brandsData.length === 0 ? (
                  <EmptyState message="No citation data found. Run audits with citation-enabled models to populate this module." />
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 10 }}>
                      <thead>
                        <tr>
                          <th style={tableThStyle}>Brand</th>
                          <th style={{ ...tableThStyle, textAlign: "right" }}>Total Citations</th>
                          <th style={{ ...tableThStyle, textAlign: "right" }}>Audits</th>
                          <th style={{ ...tableThStyle, textAlign: "right" }}>Unique URLs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brandsData.map((b) => (
                          <tr
                            key={b.brand_id}
                            onClick={() => {
                              setSelectedBrandId(b.brand_id);
                              setSelectedAuditId(null);
                              setAuditsExpanded(true);
                            }}
                            style={{ cursor: "pointer" }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#f9fafb"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                          >
                            <td style={tableTdStyle}>
                              <span style={{ fontWeight: 600, color: NAVY }}>{b.brand_name}</span>
                            </td>
                            <td style={{ ...tableTdStyle, textAlign: "right", fontWeight: 600 }}>{b.total_citations}</td>
                            <td style={{ ...tableTdStyle, textAlign: "right" }}>{b.audits}</td>
                            <td style={{ ...tableTdStyle, textAlign: "right" }}>{b.unique_urls}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* Brand selected - domain table */}
            {!loading && selectedBrandId !== null && (
              <>
                {displayDomains.length === 0 ? (
                  <EmptyState
                    message={
                      search.trim()
                        ? "No domains match your search."
                        : "No citation data for this brand yet."
                    }
                  />
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 10 }}>
                      <thead>
                        <tr>
                          {editMode && (
                            <th style={{ padding: "10px 6px", borderBottom: "2px solid #e5e7eb", width: 36, textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={selectedForReclassify.size > 0 && selectedForReclassify.size === displayDomains.length}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedForReclassify(new Set(displayDomains.map((d) => d.domain)));
                                  else setSelectedForReclassify(new Set());
                                }}
                              />
                            </th>
                          )}
                          <SortHeader label="Domain" field="domain" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                          <SortHeader label="Category" field="category" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={{ width: 140 }} />
                          <SortHeader label="Appearances" field="appearances" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={{ textAlign: "right", width: 110 }} />
                          <SortHeader label="Audit Spread" field="audit_spread" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={{ textAlign: "right", width: 110 }} />
                          <SortHeader label="URLs" field="unique_urls" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={{ textAlign: "right", width: 80 }} />
                          <th
                            style={{
                              padding: "10px 12px",
                              textAlign: "center",
                              fontSize: 12,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                              color: "#666",
                              borderBottom: "2px solid #e5e7eb",
                              width: 70,
                            }}
                          >
                            Owned
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayDomains.map((d, i) => {
                          const isOwned = ownedDomains.includes(d.domain);
                          const crossBrandCount = d.brand_spread || 0;
                          return (
                            <tr
                              key={d.domain || i}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "#f9fafb"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                              style={editMode && selectedForReclassify.has(d.domain) ? { background: "#eff6ff" } : undefined}
                            >
                              {editMode && (
                                <td style={{ ...tableTdStyle, textAlign: "center", width: 36, padding: "8px 6px" }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedForReclassify.has(d.domain)}
                                    onChange={(e) => {
                                      setSelectedForReclassify((prev) => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(d.domain);
                                        else next.delete(d.domain);
                                        return next;
                                      });
                                    }}
                                  />
                                </td>
                              )}
                              <td style={tableTdStyle}>
                                <span
                                  onClick={() => setModalDomain(d.domain)}
                                  style={{
                                    color: "#2563eb",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                    fontSize: 13,
                                  }}
                                >
                                  {d.domain}
                                </span>
                                {crossBrandCount > 1 && (
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      fontSize: 10,
                                      fontWeight: 600,
                                      background: "#e0e7ff",
                                      color: "#3730a3",
                                      borderRadius: 8,
                                      padding: "1px 6px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {crossBrandCount} brands
                                  </span>
                                )}
                              </td>
                              <td style={tableTdStyle}>
                                {editMode ? (
                                  <select
                                    value={d.category || "other"}
                                    onChange={async (e) => {
                                      const newCat = e.target.value;
                                      try {
                                        const { citationsUpdateCategory } = await import("../services/api");
                                        await citationsUpdateCategory(d.domain, newCat);
                                        setDomains((prev) => prev.map((dd) => dd.domain === d.domain ? { ...dd, category: newCat } : dd));
                                        setFeedback({ type: "success", message: `${d.domain} → ${newCat.replace(/_/g, " ")} (admin locked)` });
                                      } catch (err) {
                                        setFeedback({ type: "error", message: err?.message || `Failed to update ${d.domain}` });
                                      }
                                    }}
                                    style={{
                                      padding: "3px 6px", fontSize: 12, borderRadius: 6,
                                      border: "1px solid #d1d5db", background: "#fff", cursor: "pointer",
                                    }}
                                  >
                                    {["brand_site", "comparison_aggregator", "news_editorial", "social_video", "forum", "e_commerce", "authority", "other"].map((c) => (
                                      <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <CategoryBadge category={d.category} />
                                )}
                              </td>
                              <td style={{ ...tableTdStyle, textAlign: "right", fontWeight: 600 }}>
                                {d.appearances}
                              </td>
                              <td style={{ ...tableTdStyle, textAlign: "right" }}>
                                {d.audit_spread}
                              </td>
                              <td style={{ ...tableTdStyle, textAlign: "right" }}>
                                {d.unique_urls}
                              </td>
                              <td style={{ ...tableTdStyle, textAlign: "center" }}>
                                {isAdmin ? (
                                  <button
                                    onClick={() => toggleOwned(d.domain, isOwned)}
                                    title={isOwned ? "Remove owned status" : "Mark as owned"}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      fontSize: 16,
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                      transition: "background 0.1s",
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f4f6"; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
                                  >
                                    {isOwned ? "\u2705" : "\u2B1C"}
                                  </button>
                                ) : (
                                  <span style={{ fontSize: 16 }}>
                                    {isOwned ? "\u2705" : ""}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Domain detail modal */}
      {modalDomain && (
        <DomainModal
          domain={modalDomain}
          brandId={selectedBrandId}
          brandName={selectedBrandId ? (brandsData.find((b) => b.brand_id === selectedBrandId)?.brand_name || null) : null}
          auditId={selectedAuditId}
          onClose={() => setModalDomain(null)}
        />
      )}
    </AppShell>
  );
}

const tableThStyle = {
  padding: "10px 12px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: "#666",
  borderBottom: "2px solid #e5e7eb",
};

const tableTdStyle = {
  padding: "10px 12px",
  fontSize: 13,
  borderBottom: "1px solid #f3f4f6",
  color: NAVY,
};
